// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import clearMessages from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/ClearMessages";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import setInitialValues from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetInitialValues";
import setIsPreloadingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetIsPreloadingData";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import CheckoutRecaptcha from "@steiner/App/Components/Checkout/CheckoutRecaptcha";
import trackBeginCheckout from "@steiner/App/GoogleAnalyticsConnector/Handlers/Overrides/CheckoutShipping/TrackBeginCheckout";
import { scrollToFirstFormError } from "@steiner/App/Store/Selectors/CustomerSelectors";
import StepperContextProvider from "@steiner/App/Widgets/CheckoutStepper/StepperContext";
import { parseBoolean } from "@steiner/XDL/Utilities";
import React, { Component, createContext } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    preloadCheckoutShippingData: makeHandlerChainAwaitable(preloadCheckoutShippingData),
    setIsPreloadingData,
    updateCart: makeHandlerChainAwaitable(updateCart),
    setInitialValues,
    clearMessages,
    // XNG-Changes
    trackBeginCheckout,
};

const mapStateToProps = (state: ApplicationState) => {
    const session = state.context.session;
    return {
        cartId: state.pages.checkoutShipping.cartId,
        isPreloadingData: state.pages.checkoutShipping.isPreloadingData,
        reviewAndSubmitPageLink: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage"),
        location: getLocation(state),
        isUpdatingCustomer: state.pages.checkoutShipping.isUpdatingCustomer,
        session,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        // XNG-Changes: STR-2731
        enableCheckoutRecaptcha: parseBoolean(session?.properties?.checkoutRecaptchaRequired),
    };
};

type Props = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

type Validator = () => Promise<boolean>;

type State = {
    formSubmitAttempted: boolean;
    validators: {
        [key: string]: Validator | undefined;
    };
    isRedirecting: boolean;
    // XNG-Changes: STR-2731
    checkoutRecaptchaUnlocked: boolean;
};

export const CheckoutShippingFormContext = createContext<State>({
    formSubmitAttempted: false,
    validators: {},
    isRedirecting: false,
    // XNG-Changes: STR-2731
    checkoutRecaptchaUnlocked: false,
});

class CheckoutShippingPage extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        // XNG-Changes: STR-2731
        let checkoutRecaptchaUnlocked = false;
        if (typeof window !== "undefined" && props.enableCheckoutRecaptcha) {
            checkoutRecaptchaUnlocked = !!getCookie("g-recaptcha-response");
        }
        this.state = {
            formSubmitAttempted: false,
            validators: {},
            isRedirecting: false,
            // XNG-Changes: STR-2731
            checkoutRecaptchaUnlocked,
        };
    }

    initializeCheckoutShipping = async () => {
        if (!this.props.isPreloadingData) {
            const { cartId } = parseQueryString<{ cartId?: string }>(this.props.location.search);
            await this.props.preloadCheckoutShippingData({ cartId });
            this.props.setInitialValues();
            // XNG-Changes
            this.props.trackBeginCheckout({});
        }
        this.props.setIsPreloadingData({ isPreloadingData: false });
        this.props.clearMessages();
    };

    async componentDidMount() {
        if (this.props.enableCheckoutRecaptcha && !this.state.checkoutRecaptchaUnlocked) {
            this.props.setIsPreloadingData({ isPreloadingData: false });
            this.props.clearMessages();
            return;
        }
        await this.initializeCheckoutShipping();
    }

    handleCheckoutRecaptchaVerified = async () => {
        this.setState({ checkoutRecaptchaUnlocked: true });
        await this.initializeCheckoutShipping();
    };

    handleFormSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();

        if (!this.props.session.isAuthenticated) {
            window.location.reload();
            return;
        }

        this.setState({ formSubmitAttempted: true });

        const validators: Validator[] = [];
        Object.keys(this.state.validators).forEach(key => {
            const validator = this.state.validators[key];
            if (validator) {
                validators.push(validator);
            }
        });
        const results = await Promise.all(validators.map(v => v()));

        if (results.every(o => o)) {
            this.setState({ isRedirecting: true });
            // TODO ISC-12556
            await this.props.updateCart({});

            const continueUrl = this.props.cartId
                ? `${this.props.reviewAndSubmitPageLink!.url}?cartId=${this.props.cartId}`
                : this.props.reviewAndSubmitPageLink!.url;
            this.props.history.push(continueUrl);
        } else {
            scrollToFirstFormError();
        }
    };

    // XNG-Changes
    // parse handleFormSubmit to the StepperContextProvider
    render() {
        // XNG-Changes: STR-2731
        const showCheckoutRecaptcha = this.props.enableCheckoutRecaptcha && !this.state.checkoutRecaptchaUnlocked;

        if (showCheckoutRecaptcha) {
            return (
                <StepperContextProvider handleFormSubmit={this.handleFormSubmit}>
                    <Page data-test-selector="checkoutShippingRecaptcha">
                        <CheckoutRecaptcha onVerified={this.handleCheckoutRecaptchaVerified} />
                    </Page>
                </StepperContextProvider>
            );
        }

        return (
            <StepperContextProvider handleFormSubmit={this.handleFormSubmit}>
                <Page data-test-selector="checkoutShipping">
                    <LoadingOverlay loading={this.props.isUpdatingCustomer}>
                        <CheckoutShippingFormContext.Provider value={this.state}>
                            <form id="checkoutShippingForm" onSubmit={this.handleFormSubmit} noValidate>
                                <Zone zoneName="Content" contentId={this.props.id} />
                            </form>
                        </CheckoutShippingFormContext.Provider>
                    </LoadingOverlay>
                </Page>
            </StepperContextProvider>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutShippingPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "CheckoutShippingPage" instead of this constant.
 */
export const CheckoutShippingPageContext = "CheckoutShippingPage";
